/**
 * Copyright (c) 2017-2020 mol* contributors, licensed under MIT, See LICENSE file for more info.
 *
 * @author David Sehnal <david.sehnal@gmail.com>
 * @author Alexander Rose <alexander.rose@weirdbyte.de>
 */
import { __awaiter, __generator } from "tslib";
import { SortedArray } from '../../../mol-data/int';
import { EquivalenceClasses } from '../../../mol-data/util';
import { Spacegroup, SpacegroupCell, SymmetryOperator } from '../../../mol-math/geometry';
import { Vec3, Mat4 } from '../../../mol-math/linear-algebra';
import { Task } from '../../../mol-task';
import { Symmetry, Model } from '../model';
import { QueryContext, StructureSelection, Queries as Q } from '../query';
import { Structure } from './structure';
import { Unit } from './unit';
import { ModelSymmetry } from '../../../mol-model-formats/structure/property/symmetry';
import { StructureProperties } from './properties';
var StructureSymmetry;

(function (StructureSymmetry) {
  function buildAssembly(structure, asmName) {
    var _this = this;

    return Task.create('Build Assembly', function (ctx) {
      return __awaiter(_this, void 0, void 0, function () {
        var models, assembly, coordinateSystem, assembler, queryCtx, _i, _a, g, selection, units, _b, _c, oper, _d, units_1, unit;

        return __generator(this, function (_e) {
          models = structure.models;
          if (models.length !== 1) throw new Error('Can only build assemblies from structures based on 1 model.');
          assembly = Symmetry.findAssembly(models[0], asmName);
          if (!assembly) throw new Error("Assembly '" + asmName + "' is not defined.");
          coordinateSystem = SymmetryOperator.create(assembly.id, Mat4.identity(), {
            assembly: {
              id: assembly.id,
              operId: 0,
              operList: []
            }
          });
          assembler = Structure.Builder({
            coordinateSystem: coordinateSystem,
            label: structure.label
          });
          queryCtx = new QueryContext(structure);

          for (_i = 0, _a = assembly.operatorGroups; _i < _a.length; _i++) {
            g = _a[_i];
            selection = g.selector(queryCtx);

            if (StructureSelection.structureCount(selection) === 0) {
              continue;
            }

            units = StructureSelection.unionStructure(selection).units;

            for (_b = 0, _c = g.operators; _b < _c.length; _b++) {
              oper = _c[_b];

              for (_d = 0, units_1 = units; _d < units_1.length; _d++) {
                unit = units_1[_d];
                assembler.addWithOperator(unit, oper);
              }
            }
          }

          return [2
          /*return*/
          , assembler.getStructure()];
        });
      });
    });
  }

  StructureSymmetry.buildAssembly = buildAssembly;

  function buildSymmetryAssembly(structure, generators, symmetry) {
    var _this = this;

    return Task.create('Build Symmetry Assembly', function (ctx) {
      return __awaiter(_this, void 0, void 0, function () {
        var models, modelCenter, assembler, queryCtx, _i, generators_1, g, selector, selection, units, _a, _b, _c, index, _d, i, j, k, operators, _e, units_2, unit, _f, operators_1, op;

        return __generator(this, function (_g) {
          models = structure.models;
          if (models.length !== 1) throw new Error('Can only build symmetry assemblies from structures based on 1 model.');
          modelCenter = Vec3();
          assembler = Structure.Builder({
            label: structure.label,
            representativeModel: models[0]
          });
          queryCtx = new QueryContext(structure);

          for (_i = 0, generators_1 = generators; _i < generators_1.length; _i++) {
            g = generators_1[_i];
            selector = getSelector(g.asymIds);
            selection = selector(queryCtx);

            if (StructureSelection.structureCount(selection) === 0) {
              continue;
            }

            units = StructureSelection.unionStructure(selection).units;

            for (_a = 0, _b = g.operators; _a < _b.length; _a++) {
              _c = _b[_a], index = _c.index, _d = _c.shift, i = _d[0], j = _d[1], k = _d[2];
              operators = getOperatorsForIndex(symmetry, index, i, j, k, modelCenter);

              for (_e = 0, units_2 = units; _e < units_2.length; _e++) {
                unit = units_2[_e];

                for (_f = 0, operators_1 = operators; _f < operators_1.length; _f++) {
                  op = operators_1[_f];
                  assembler.addWithOperator(unit, op);
                }
              }
            }
          }

          return [2
          /*return*/
          , assembler.getStructure()];
        });
      });
    });
  }

  StructureSymmetry.buildSymmetryAssembly = buildSymmetryAssembly;

  function builderSymmetryMates(structure, radius) {
    return Task.create('Find Symmetry Mates', function (ctx) {
      return findMatesRadius(ctx, structure, radius);
    });
  }

  StructureSymmetry.builderSymmetryMates = builderSymmetryMates;

  function buildSymmetryRange(structure, ijkMin, ijkMax) {
    return Task.create('Build Symmetry', function (ctx) {
      return findSymmetryRange(ctx, structure, ijkMin, ijkMax);
    });
  }

  StructureSymmetry.buildSymmetryRange = buildSymmetryRange;
  /** Builds NCS structure, returns the original if NCS operators are not present. */

  function buildNcs(structure) {
    return Task.create('Build NCS', function (ctx) {
      return _buildNCS(ctx, structure);
    });
  }

  StructureSymmetry.buildNcs = buildNcs;

  function areUnitsEquivalent(a, b) {
    return a.invariantId === b.invariantId && a.model.id === b.model.id && SortedArray.areEqual(a.elements, b.elements);
  }

  StructureSymmetry.areUnitsEquivalent = areUnitsEquivalent;

  function UnitEquivalenceBuilder() {
    return EquivalenceClasses(Unit.hashUnit, areUnitsEquivalent);
  }

  StructureSymmetry.UnitEquivalenceBuilder = UnitEquivalenceBuilder;

  function computeTransformGroups(s) {
    var groups = UnitEquivalenceBuilder();

    for (var _i = 0, _a = s.units; _i < _a.length; _i++) {
      var u = _a[_i];
      groups.add(u.id, u);
    }

    var ret = [];

    for (var _b = 0, _c = groups.groups; _b < _c.length; _b++) {
      var eqUnits = _c[_b];
      ret.push(Unit.SymmetryGroup(eqUnits.map(function (id) {
        return s.unitMap.get(id);
      })));
    }

    return ret;
  }

  StructureSymmetry.computeTransformGroups = computeTransformGroups;
  /** Checks if transform groups are equal up to their unit's transformations */

  function areTransformGroupsEquivalent(a, b) {
    if (a.length !== b.length) return false;

    for (var i = 0, il = a.length; i < il; ++i) {
      var au = a[i].units,
          bu = b[i].units;
      if (au.length !== bu.length) return false;
      if (a[i].hashCode !== b[i].hashCode) return false;

      for (var j = 0, _j = au.length; j < _j; j++) {
        if (au[j].conformation !== bu[j].conformation) return false;
      }
    }

    return true;
  }

  StructureSymmetry.areTransformGroupsEquivalent = areTransformGroupsEquivalent;
})(StructureSymmetry || (StructureSymmetry = {}));

function getSelector(asymIds) {
  return Q.generators.atoms({
    chainTest: Q.pred.and(Q.pred.eq(function (ctx) {
      return StructureProperties.unit.operator_name(ctx.element);
    }, SymmetryOperator.DefaultName), Q.pred.inSet(function (ctx) {
      return StructureProperties.chain.label_asym_id(ctx.element);
    }, asymIds))
  });
}

function getOperatorsForIndex(symmetry, index, i, j, k, modelCenter) {
  var spacegroup = symmetry.spacegroup,
      ncsOperators = symmetry.ncsOperators;
  var operators = [];
  var toFractional = spacegroup.cell.toFractional;
  var ref = Vec3.transformMat4(Vec3(), modelCenter, toFractional);
  var symOp = Spacegroup.getSymmetryOperatorRef(spacegroup, index, i, j, k, ref);

  if (ncsOperators && ncsOperators.length) {
    for (var u = 0, ul = ncsOperators.length; u < ul; ++u) {
      var ncsOp = ncsOperators[u];
      var matrix = Mat4.mul(Mat4(), symOp.matrix, ncsOp.matrix);
      var operator = SymmetryOperator.create(symOp.name + " " + ncsOp.name, matrix, {
        assembly: symOp.assembly,
        ncsId: ncsOp.ncsId,
        hkl: symOp.hkl,
        spgrOp: symOp.spgrOp
      });
      operators.push(operator);
    }
  } else {
    operators.push(symOp);
  }

  return operators;
}

function getOperatorsForRange(symmetry, ijkMin, ijkMax, modelCenter) {
  var spacegroup = symmetry.spacegroup,
      ncsOperators = symmetry.ncsOperators;
  var ncsCount = ncsOperators && ncsOperators.length || 0;
  var operators = [];

  if (!ncsCount && ijkMin[0] <= 0 && ijkMax[0] >= 0 && ijkMin[1] <= 0 && ijkMax[1] >= 0 && ijkMin[2] <= 0 && ijkMax[2] >= 0) {
    operators[0] = Spacegroup.getSymmetryOperator(spacegroup, 0, 0, 0, 0);
  }

  var toFractional = spacegroup.cell.toFractional;
  var ref = Vec3.transformMat4(Vec3(), modelCenter, toFractional);

  for (var op = 0; op < spacegroup.operators.length; op++) {
    for (var i = ijkMin[0]; i <= ijkMax[0]; i++) {
      for (var j = ijkMin[1]; j <= ijkMax[1]; j++) {
        for (var k = ijkMin[2]; k <= ijkMax[2]; k++) {
          // check if we have added identity as the 1st operator.
          if (!ncsCount && op === 0 && i === 0 && j === 0 && k === 0) continue;
          operators.push.apply(operators, getOperatorsForIndex(symmetry, op, i, j, k, ref));
        }
      }
    }
  }

  return operators;
}

function getOperatorsCached333(symmetry, ref) {
  if (symmetry._operators_333 && Vec3.equals(ref, symmetry._operators_333.ref)) {
    return symmetry._operators_333.operators;
  }

  symmetry._operators_333 = {
    ref: Vec3.clone(ref),
    operators: getOperatorsForRange(symmetry, Vec3.create(-3, -3, -3), Vec3.create(3, 3, 3), ref)
  };
  return symmetry._operators_333.operators;
}

function assembleOperators(structure, operators) {
  var assembler = Structure.Builder({
    label: structure.label
  });
  var units = structure.units;

  for (var _i = 0, operators_2 = operators; _i < operators_2.length; _i++) {
    var oper = operators_2[_i];

    for (var _a = 0, units_3 = units; _a < units_3.length; _a++) {
      var unit = units_3[_a];
      assembler.addWithOperator(unit, oper);
    }
  }

  return assembler.getStructure();
}

function _buildNCS(ctx, structure) {
  return __awaiter(this, void 0, void 0, function () {
    var models, symmetry, operators;
    return __generator(this, function (_a) {
      models = structure.models;
      if (models.length !== 1) throw new Error('Can only build NCS from structures based on 1 model.');
      symmetry = ModelSymmetry.Provider.get(models[0]);
      if (!symmetry) return [2
      /*return*/
      , structure];
      operators = symmetry.ncsOperators;
      if (!operators || !operators.length) return [2
      /*return*/
      , structure];
      return [2
      /*return*/
      , assembleOperators(structure, operators)];
    });
  });
}

function findSymmetryRange(ctx, structure, ijkMin, ijkMax) {
  return __awaiter(this, void 0, void 0, function () {
    var models, symmetry, spacegroup, modelCenter, operators;
    return __generator(this, function (_a) {
      models = structure.models;
      if (models.length !== 1) throw new Error('Can only build symmetries from structures based on 1 model.');
      symmetry = ModelSymmetry.Provider.get(models[0]);
      if (!symmetry) return [2
      /*return*/
      , structure];
      spacegroup = symmetry.spacegroup;
      if (SpacegroupCell.isZero(spacegroup.cell)) return [2
      /*return*/
      , structure];
      modelCenter = Model.getCenter(models[0]);
      operators = getOperatorsForRange(symmetry, ijkMin, ijkMax, modelCenter);
      return [2
      /*return*/
      , assembleOperators(structure, operators)];
    });
  });
}

function findMatesRadius(ctx, structure, radius) {
  return __awaiter(this, void 0, void 0, function () {
    function hash(unit, oper) {
      return unit.invariantId + "|" + oper.name;
    }

    var models, symmetry, spacegroup, modelCenter, operators, lookup, added, assembler, units, center, _i, operators_3, oper, _a, units_4, unit, boundingSphere, closeUnits, uI, _uI, closeUnit, h;

    return __generator(this, function (_b) {
      switch (_b.label) {
        case 0:
          models = structure.models;
          if (models.length !== 1) throw new Error('Can only build symmetries from structures based on 1 model.');
          symmetry = ModelSymmetry.Provider.get(models[0]);
          if (!symmetry) return [2
          /*return*/
          , structure];
          spacegroup = symmetry.spacegroup;
          if (SpacegroupCell.isZero(spacegroup.cell)) return [2
          /*return*/
          , structure];
          if (!ctx.shouldUpdate) return [3
          /*break*/
          , 2];
          return [4
          /*yield*/
          , ctx.update('Initialing...')];

        case 1:
          _b.sent();

          _b.label = 2;

        case 2:
          modelCenter = Model.getCenter(models[0]);
          operators = getOperatorsCached333(symmetry, modelCenter);
          lookup = structure.lookup3d;
          added = new Set();
          assembler = Structure.Builder({
            label: structure.label
          });
          units = structure.units;
          center = Vec3.zero();
          _i = 0, operators_3 = operators;
          _b.label = 3;

        case 3:
          if (!(_i < operators_3.length)) return [3
          /*break*/
          , 6];
          oper = operators_3[_i];

          for (_a = 0, units_4 = units; _a < units_4.length; _a++) {
            unit = units_4[_a];
            boundingSphere = unit.lookup3d.boundary.sphere;
            Vec3.transformMat4(center, boundingSphere.center, oper.matrix);
            closeUnits = lookup.findUnitIndices(center[0], center[1], center[2], boundingSphere.radius + radius);

            for (uI = 0, _uI = closeUnits.count; uI < _uI; uI++) {
              closeUnit = units[closeUnits.indices[uI]];
              if (!closeUnit.lookup3d.check(center[0], center[1], center[2], boundingSphere.radius + radius)) continue;
              h = hash(unit, oper);

              if (!added.has(h)) {
                assembler.addWithOperator(unit, oper);
                added.add(h);
              }
            }
          }

          if (!ctx.shouldUpdate) return [3
          /*break*/
          , 5];
          return [4
          /*yield*/
          , ctx.update('Building symmetry...')];

        case 4:
          _b.sent();

          _b.label = 5;

        case 5:
          _i++;
          return [3
          /*break*/
          , 3];

        case 6:
          return [2
          /*return*/
          , assembler.getStructure()];
      }
    });
  });
}

export { StructureSymmetry };